import {
    BarChartOutlined, DashboardOutlined, GlobalOutlined, HomeOutlined, LogoutOutlined,
    MenuOutlined, MoonOutlined, RocketOutlined, SunOutlined, UserOutlined
} from "@ant-design/icons";
import { Avatar, Button, Dropdown, Grid, Layout, Menu } from "antd";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";

const { Header } = Layout;

const Navigation = ({ activeTab, onTabChange, isDarkMode, onToggleTheme }) => {
  const { language, toggleLanguage, t } = useLanguage();
  const { user, logout, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();

  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  const handleMenuClick = ({ key }) => {
    navigate(`/${key}`);
    if (typeof onTabChange === "function") {
      onTabChange(key);
    }
  };

  const handleLogin = () => {
    navigate("/login");
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const userMenuItems = [
    {
      key: "profile",
      icon: <UserOutlined />,
      label: user?.name || user?.email || "Profile",
      disabled: true,
    },
    {
      type: "divider",
    },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: t("auth.logout"),
      onClick: handleLogout,
    },
  ];

  const menuItems = [
    {
      key: "home",
      icon: <HomeOutlined />,
      label: t("nav.home"),
    },
    {
      key: "jobs",
      icon: <DashboardOutlined />,
      label: t("nav.dashboard"),
    },
    {
      key: "analytics",
      icon: <BarChartOutlined />,
      label: t("nav.analytics"),
    },
    {
      key: "apply",
      icon: <RocketOutlined />,
      label: t("nav.applyService"),
    },
  ];

  return (
    <Header
      style={{
        background: isDarkMode ? "#141414" : "#fff",
        padding: isMobile ? "0 8px" : "0 24px",
        borderBottom: isDarkMode ? "1px solid #303030" : "1px solid #f0f0f0",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        position: "sticky",
        top: 0,
        zIndex: 1000,
        boxShadow: isDarkMode
          ? "0 2px 8px rgba(0,0,0,0.3)"
          : "0 2px 8px rgba(0,0,0,0.06)",
      }}
    >
      {/* Logo/Brand Section */}
      <div style={{ display: "flex", alignItems: "center", gap: isMobile ? "8px" : "12px" }}>
        <div
          style={{
            width: 40,
            height: 40,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            borderRadius: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span style={{ color: "white", fontWeight: "bold", fontSize: 18 }}>
            JH
          </span>
        </div>
        <h1
          style={{
            margin: 0,
            fontSize: 20,
            fontWeight: 600,
            color: isDarkMode ? "#fff" : "#1f2937",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: isMobile ? "120px" : "none",
          }}
        >
          {t("nav.title")}
        </h1>
      </div>

      {/* Navigation Menu - Desktop */}
      {!isMobile && (
        <Menu
          mode="horizontal"
          selectedKeys={[activeTab]}
          items={menuItems}
          onClick={handleMenuClick}
          style={{
            flex: 1,
            justifyContent: "center",
            border: "none",
            background: "transparent",
            minWidth: 0,
            lineHeight: "64px",
            marginLeft: 24,
            marginRight: 24,
          }}
          theme={isDarkMode ? "dark" : "light"}
        />
      )}

      {/* Right Section: Mobile Menu + Theme Toggle + User */}
      <div style={{ display: "flex", alignItems: "center", gap: isMobile ? "4px" : "24px" }}>

        {/* Mobile Menu Trigger */}
        {isMobile && (
          <Dropdown
            menu={{
              items: menuItems,
              onClick: handleMenuClick,
              selectedKeys: [activeTab]
            }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button
              type="text"
              icon={<MenuOutlined />}
              style={{ fontSize: 20 }}
            />
          </Dropdown>
        )}


        {/* Language Toggle */}
        <Button
          type="text"
          onClick={toggleLanguage}
          className="language-toggle-btn"
          style={{
            fontSize: isMobile ? 16 : 20,
            width: isMobile ? 36 : 64,
            height: 40,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
            transition: "all 0.3s ease",
            padding: isMobile ? "0 4px" : "4px 15px"
          }}
          title={language === "en" ? "切换到中文" : "Switch to English"}
        >
          <GlobalOutlined style={{ fontSize: isMobile ? 16 : 18 }} />
          {!isMobile && (
            <span
              className="language-label"
              style={{ fontSize: 12, fontWeight: 600 }}
            >
              {language.toUpperCase()}
            </span>
          )}
        </Button>

        {/* Theme Toggle */}
        <Button
          type="text"
          icon={isDarkMode ? <SunOutlined /> : <MoonOutlined />}
          onClick={onToggleTheme}
          className="theme-toggle-btn"
          style={{
            fontSize: 20,
            width: 40,
            height: 40,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.3s ease",
          }}
        />

        {/* User Profile / Auth Controls */}
        {loading ? (
          <Avatar
            style={{
              backgroundColor: "#d1d5db",
              cursor: "default",
              transition: "all 0.3s ease",
            }}
            icon={<UserOutlined />}
          />
        ) : isAuthenticated ? (
          <Dropdown
            menu={{ items: userMenuItems }}
            placement="bottomRight"
            trigger={["click"]}
          >
            <Avatar
              style={{
                backgroundColor: "#667eea",
                cursor: "pointer",
                transition: "all 0.3s ease",
              }}
              icon={<UserOutlined />}
            >
              {user?.name?.[0] || user?.email?.[0] || "U"}
            </Avatar>
          </Dropdown>
        ) : (
          <Button
            type="primary"
            onClick={handleLogin}
            style={{
              fontWeight: 600,
              height: 40,
              display: "flex",
              alignItems: "center",
              padding: "0 20px",
            }}
          >
            {t("auth.login")}
          </Button>
        )}
      </div>
    </Header >
  );
};

export default Navigation;
